import moment from "moment";
import { DASHBOARD_EPP } from "@/apollo/queries";
import { exQue } from "@/utils/gql";
import { formatArrayText } from "@/utils/formats";
import { fechaConHora, hoyChile } from "@/utils/time";
import { normalizeStateName } from "./solicitudes.js";

// Estados que todavía esperan aprobación o entrega. El dashboard agrupa las
// solicitudes por el nombre del estado, así que se comparan por nombre.
const ESTADOS_PENDIENTES = ["solicitado", "en proceso", "en proceso de entrega"];

// FindLotesPorVencer devuelve como máximo esta cantidad de lotes
const LIMITE_LOTES_POR_VENCER = 10;

// Número de la última consulta del dashboard. Al cambiar rápido de bodega o de
// fechas, una respuesta vieja puede llegar después de la nueva (volver a una
// bodega ya vista responde al instante desde la caché): solo se muestra la última
let ultimaConsultaDashboard = 0;

// La fecha de vencimiento viene a las 00:00 UTC: el día es la parte de la
// fecha del ISO (en hora de Chile sería el día anterior)
const diaVencimiento = (fecha) => {
  const date = new Date(fecha);
  return fecha && !Number.isNaN(date.getTime())
    ? date.toISOString().slice(0, 10)
    : null;
};

// Días entre dos fechas YYYY-MM-DD (Date.parse las toma en UTC)
const diasEntre = (desde, hasta) =>
  Math.round((Date.parse(hasta) - Date.parse(desde)) / 86400000);

// Gris para lo que no tiene color propio: Cancelada, estados desconocidos y
// el sector "Otras" de las categorías
const GRIS = "#898781";

// Colores de los estados cuando la base no trae uno distinto para cada uno.
// Fijos por estado, de una paleta categórica validada: es el grupo de cinco
// con mejor separación entre todos los pares, porque en la dona cualquier
// estado queda al lado de otro si faltan los del medio.
const COLORES_ESTADO = {
  solicitado: "#eda100",
  "en proceso": "#2a78d6",
  "en proceso de entrega": "#1baf7a",
  entregado: "#008300",
  rechazada: "#e34948",
  cancelada: GRIS,
};
// Otros estados del formulario, en su orden; después de estos, gris
const COLORES_ESTADO_OTROS = ["#e87ba4", "#eb6834", "#6250d6"];

// Categorías: color por posición (de más a menos unidades), en el orden fijo
// de la paleta validada. Los sectores siempre usan colores consecutivos, así
// que basta con que pasen los pares vecinos (validados como anillo).
const COLORES_CATEGORIA = [
  "#2a78d6",
  "#eb6834",
  "#1baf7a",
  "#eda100",
  "#e87ba4",
  "#008300",
];

const parseCategorias = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return [];
  }

  return Object.entries(value)
    .map(([nombre, detalle]) => {
      const d = detalle && typeof detalle === "object" ? detalle : {};
      return {
        nombre,
        cantidad: Number(d.cantidad) || 0,
        precio_total: Number(d.precio_total) || 0,
        porcentaje_cantidad: Number(d.porcentaje_cantidad) || 0,
        porcentaje_precio: Number(d.porcentaje_precio) || 0,
      };
    })
    .sort((a, b) => b.cantidad - a.cantidad);
};

const parseListProductos = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return [];
  }

  return Object.entries(value).map(([nombre, detalle]) => {
    const detalleProducto =
      detalle && typeof detalle === "object" ? detalle : {};

    return {
      nombre,
      cantidad: Number(detalleProducto.cantidad) || 0,
      precio_unitario: Number(detalleProducto.precio_unitario) || 0,
      precio_total: Number(detalleProducto.precio_total) || 0,
    };
  });
};

export const dashState = {
  loading: false,
  dashboardData: {
    totalSolicitudes: 0,
    solicitudesByEstado: {},
    solicitudesByTurno: [],
    solicitudesByAreaAndMonth: [],
    listProductos: {},
    solicitudesByGeneroAndTalla: [],
    totalProductosByCategoria: {},
    topLotesVencimiento: [],
    topSolicitantesPorArea: [],
    topEPPSolicitados: [],
    totalProductosDesgloceByCategoria: [],
  },
  productBoxes: [],
  productTotal: 0,
  categoriaBoxes: [],
  keyChartEstado: 0,
  // Desde cuántos días antes de vencer un lote es urgente o está por vencer.
  // Fijos por ahora; después serán configurables.
  diasVencimiento: { urgente: 7, porVencer: 30 },
};

export const dashMutations = {
  SET_DASHBOARD_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_DASHBOARD_EPP(state, payload) {
    state.dashboardData = {
      ...state.dashboardData,
      ...payload,
    };

    const productosData = parseListProductos(state.dashboardData.listProductos);
    const detalleGeneroTalla = Array.isArray(
      state.dashboardData.solicitudesByGeneroAndTalla,
    )
      ? state.dashboardData.solicitudesByGeneroAndTalla
      : [];

    const boxes = productosData.map((producto) => {
      const detalleProducto =
        detalleGeneroTalla.find((item) => item?.Producto === producto.nombre) ||
        {};
      const tallas =
        detalleProducto.totalByTalla &&
          typeof detalleProducto.totalByTalla === "object"
          ? Object.keys(detalleProducto.totalByTalla)
          : [];
      const generos =
        detalleProducto.totalByGenero &&
          typeof detalleProducto.totalByGenero === "object"
          ? Object.keys(detalleProducto.totalByGenero)
          : [];


      return {
        nombre: producto.nombre,
        cantidad: producto.cantidad,
        precio_unitario: producto.precio_unitario,
        precio_total: producto.precio_total,
        tallas,
        generos,
      };
    });

    state.productBoxes = boxes.sort((a, b) => b.cantidad - a.cantidad);

    state.productTotal = state.productBoxes.reduce(
      (acc, item) => acc + Number(item.cantidad || 0),
      0,
    );

    state.categoriaBoxes = parseCategorias(
      state.dashboardData.totalProductosByCategoria,
    );

    state.keyChartEstado += 1;
  },
  RESET_DASHBOARD_EPP(state) {
    state.dashboardData = {
      totalSolicitudes: 0,
      solicitudesByEstado: {},
      solicitudesByTurno: [],
      solicitudesByAreaAndMonth: [],
      listProductos: {},
      solicitudesByGeneroAndTalla: [],
      totalProductosByCategoria: {},
      topLotesVencimiento: [],
      topSolicitantesPorArea: [],
      topEPPSolicitados: [],
      totalProductosDesgloceByCategoria: [],
    };
    state.productBoxes = [];
    state.productTotal = 0;
    state.categoriaBoxes = [];
    state.keyChartEstado += 1;
  },
};

export const dashActions = {
  // Devuelve true si se mostró la respuesta o si otra consulta más nueva la
  // reemplazó; undefined si falló (el Dashboard avisa con toastGenericError)
  async getDashboardEppAction({ commit, state, dispatch }, payload = {}) {
    const consulta = ++ultimaConsultaDashboard;
    const vigente = () => consulta === ultimaConsultaDashboard;
    commit("SET_DASHBOARD_LOADING", true);

    await dispatch("ensureFormEpp");

    const variables = {
      id_formulario: state.formEpp?.id_formulario,
      fecha_inicio: payload.fecha_inicio || "",
      // El backend compara contra la fecha a las 00:00: sin la hora, el
      // último día (hoy, por defecto) queda fuera
      fecha_final: fechaConHora(
        payload.fecha_final || moment().format("YYYY-MM-DD"),
        "23:59:59",
      ),
      id_extremidad: payload.id_extremidad ?? null,
      id_bodega: payload.id_bodega ?? null,
      // Se combinan con id_bodega: el Dashboard solo ofrece bodegas que
      // atienden la empresa y el centro de trabajo elegidos
      id_sociedad: payload.id_sociedad ?? null,
      id_sucursal: payload.id_sucursal ?? null,
      id_area: payload.id_area ?? null,
    };

    return exQue(DASHBOARD_EPP, variables)
      .then((res) => {
        if (vigente()) commit("SET_DASHBOARD_EPP", res);
        return true;
      })
      .catch((error) => {
          console.error(error);
        // Una consulta que ya fue reemplazada no es un error para el usuario
        if (!vigente()) return true;
        commit("RESET_DASHBOARD_EPP");
      })
      .finally(() => {
        if (vigente()) commit("SET_DASHBOARD_LOADING", false);
      });
  },
};

export const dashGetters = {
  // Se suma precio_total y no cantidad × precio_unitario: el backend agrupa
  // por nombre de producto, y si hay tallas con precios distintos
  // precio_unitario es solo el de la primera
  solicitudesPrecioTotal(state) {
    return state.productBoxes.reduce(
      (acc, item) => acc + Number(item.precio_total || 0),
      0,
    );
  },
  // Estados con solicitudes en el periodo, en el orden del formulario (el del
  // flujo) y con su color: el de la base si cada estado tiene uno distinto,
  // si no, el fijo de cada estado
  filasEstado(state) {
    const estados = [...(state.listEstadosSolicitud || [])].sort(
      (a, b) => (Number(a.orden) || 0) - (Number(b.orden) || 0),
    );
    const nombres = estados.map((estado) => normalizeStateName(estado.nombre));
    const coloresBase = estados.map((estado) =>
      String(estado.color || "").toLowerCase(),
    );
    const usarColoresBase =
      coloresBase.length > 0 &&
      coloresBase.every(Boolean) &&
      new Set(coloresBase).size === coloresBase.length;
    const sinColorFijo = nombres.filter((nombre) => !COLORES_ESTADO[nombre]);
    const colorDe = (nombre) => {
      const posicion = nombres.indexOf(nombre);
      if (usarColoresBase && posicion !== -1) return estados[posicion].color;
      return (
        COLORES_ESTADO[nombre] ||
        COLORES_ESTADO_OTROS[sinColorFijo.indexOf(nombre)] ||
        GRIS
      );
    };

    return Object.entries(state.dashboardData.solicitudesByEstado || {})
      .map(([nombre, total], i) => {
        const clave = normalizeStateName(nombre);
        // Los que no están en el formulario, al final; sin la lista de
        // estados, el orden en que llegaron
        let orden = nombres.indexOf(clave);
        if (orden === -1) orden = Number.MAX_SAFE_INTEGER;
        if (!nombres.length) orden = i;
        return {
          nombre,
          cantidad: Number(total) || 0,
          color: colorDe(clave),
          orden,
        };
      })
      .filter(({ cantidad }) => cantidad > 0)
      .sort((a, b) => a.orden - b.orden)
      .map(({ nombre, cantidad, color }) => ({ nombre, cantidad, color }));
  },
  // Categorías para la dona: hasta 6 sectores. Con más, las 5 con más
  // unidades y el resto junto en "Otras"
  filasCategoria(state) {
    const categorias = state.categoriaBoxes.filter((c) => c.cantidad > 0);
    const visibles =
      categorias.length > COLORES_CATEGORIA.length
        ? categorias.slice(0, COLORES_CATEGORIA.length - 1)
        : categorias;
    const resto = categorias.slice(visibles.length);
    const filas = visibles.map((c, i) => ({
      nombre: c.nombre,
      cantidad: c.cantidad,
      precio_total: c.precio_total,
      color: COLORES_CATEGORIA[i],
    }));
    if (resto.length) {
      filas.push({
        nombre: `Otras (${resto.length})`,
        cantidad: resto.reduce((acc, c) => acc + c.cantidad, 0),
        precio_total: resto.reduce((acc, c) => acc + c.precio_total, 0),
        color: GRIS,
        detalle: formatArrayText(resto.map((c) => c.nombre)),
      });
    }
    return filas;
  },
  solicitudesPendientes(state) {
    return Object.entries(state.dashboardData.solicitudesByEstado || {}).reduce(
      (acc, [nombre, total]) =>
        ESTADOS_PENDIENTES.includes(normalizeStateName(nombre))
          ? acc + (Number(total) || 0)
          : acc,
      0,
    );
  },
  // Lotes con stock, con el día en que vencen y los días que les quedan
  lotesPorVencer(state) {
    const hoy = hoyChile();
    const lotes = state.dashboardData.topLotesVencimiento;
    return (Array.isArray(lotes) ? lotes : [])
      .filter((lote) => Number(lote?.stock_real) > 0)
      .map((lote) => {
        const dia = diaVencimiento(lote.fecha_vencimiento);
        return { ...lote, dia, dias: dia ? diasEntre(hoy, dia) : null };
      });
  },
  // Para el indicador: cuántos vencen dentro del plazo "por vencer". Como el
  // backend manda solo los más próximos, si el último que llegó también vence
  // dentro del plazo puede haber más.
  resumenLotesPorVencer(state, getters) {
    const { porVencer } = state.diasVencimiento;
    const lotes = state.dashboardData.topLotesVencimiento;
    const recibidos = Array.isArray(lotes) ? lotes : [];
    const diaUltimo = diaVencimiento(
      recibidos[recibidos.length - 1]?.fecha_vencimiento,
    );

    return {
      total: getters.lotesPorVencer.filter(
        ({ dias }) => dias !== null && dias <= porVencer,
      ).length,
      hayMas:
        recibidos.length >= LIMITE_LOTES_POR_VENCER &&
        !!diaUltimo &&
        diasEntre(hoyChile(), diaUltimo) <= porVencer,
    };
  },
};
